import { InsuranceName } from '@orcar/common';
import {
  ALERT_MESSAGE_TYPE,
  convertToKST,
  PAGE_NAME,
  SESSION_STORAGE_KEY,
  useCreatePreReservation,
} from '@orcar/common-d2c';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { type EntryPoint, trackBookingStarted } from '@/utils/analytics';

type ReserveParams = {
  vehicleModelId: number;
  /** 예약 시작 이벤트(booking_started)용 차종 */
  carType: string;
  insuranceName: string;
  /** 검색 옵션의 인수 일시 (KST 변환 전 원본) */
  pickUpAt: string;
  /** 검색 옵션의 반납 일시 (KST 변환 전 원본) */
  dropOffAt: string;
  isWithPet: boolean;
  /** 예약 진입 경로(홈 인기차량 vs 검색결과) */
  source?: EntryPoint;
};

/**
 * 차량 선택 시 사전예약을 생성하고 곧바로 결제 페이지로 이동한다.
 * 기존 차량 상세(order_confirmation) 페이지를 제거하면서, 그 페이지가 담당하던
 * 사전예약 생성 로직을 이 훅으로 옮겨 "선택 → 결제" 흐름을 만든다.
 */
export const useReserveAndPay = () => {
  const navigate = useNavigate();
  const { mutate: createPreReservation, isPending } = useCreatePreReservation();

  const reserveAndPay = ({
    vehicleModelId,
    carType,
    insuranceName,
    pickUpAt,
    dropOffAt,
    isWithPet,
    source,
  }: ReserveParams) => {
    // 예약 시작 이벤트 트래킹 (기존 차량 상세 페이지의 결제하기 시점 → 차량 선택 시점으로 이전)
    trackBookingStarted({
      car_id: String(vehicleModelId),
      car_type: carType,
      dates: `${dayjs(pickUpAt).format('YYYY-MM-DD')} ~ ${dayjs(dropOffAt).format('YYYY-MM-DD')}`,
      is_with_pet: isWithPet,
      source,
    });

    // 새 차량 선택이므로 이전 완전면책(무제한 특약) 선택 상태는 초기화한다.
    sessionStorage.removeItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE);
    sessionStorage.setItem(
      SESSION_STORAGE_KEY.PRODUCT_ID,
      String(vehicleModelId),
    );

    createPreReservation(
      {
        vehicleModelId,
        pickUpAt: convertToKST(pickUpAt),
        dropOffAt: convertToKST(dropOffAt),
        insuranceName: insuranceName as InsuranceName,
        isWithPet: isWithPet || undefined,
      },
      {
        onSuccess: (res) => {
          sessionStorage.setItem(
            SESSION_STORAGE_KEY.PRE_RESERVATION_ID,
            res.id,
          );
          navigate(PAGE_NAME.payment);
        },
        onError: () => {
          alert(ALERT_MESSAGE_TYPE.FAIL_GET_PRODUCT);
        },
      },
    );
  };

  return { reserveAndPay, isReserving: isPending };
};
